import type { OrbitalElements } from "../types/celestialBody";
import type { Vector3Data } from "../types/astronomy";
import { rotateAroundX, rotateAroundZ } from "./coordinateSystems";
import { degToRad, normalizeAngleRad, solveEccentricAnomaly, zeroVector } from "./kepler";

/**
 * Posição perifocal → eclíptica via Rz(Ω) × Rx(i) × Rz(ω).
 */
export function rotateOrbitalToEcliptic(
  orbital: Vector3Data,
  orbit: OrbitalElements,
): Vector3Data {
  const omega = degToRad(orbit.argumentOfPeriapsisDeg);
  const i = degToRad(orbit.inclinationDeg);
  const Omega = degToRad(orbit.longitudeAscendingNodeDeg);

  let v = rotateAroundZ(orbital, omega);
  v = rotateAroundX(v, i);
  v = rotateAroundZ(v, Omega);
  return v;
}

export function calculateOrbitalPosition(
  orbit: OrbitalElements,
  julianDate: number,
): Vector3Data {
  const deltaDays = julianDate - orbit.epochJulianDate;
  const meanMotionRadPerDay = (2 * Math.PI) / orbit.orbitalPeriodDays;
  const meanAnomaly = normalizeAngleRad(
    degToRad(orbit.meanAnomalyAtEpochDeg) + meanMotionRadPerDay * deltaDays,
  );

  const E = solveEccentricAnomaly(meanAnomaly, orbit.eccentricity);
  const a = orbit.semiMajorAxisKm;
  const e = orbit.eccentricity;

  const xOrbital = a * (Math.cos(E) - e);
  const yOrbital = a * Math.sqrt(1 - e * e) * Math.sin(E);

  return rotateOrbitalToEcliptic({ x: xOrbital, y: yOrbital, z: 0 }, orbit);
}

export function sampleOrbit(
  orbit: OrbitalElements,
  sampleCount: number,
): Vector3Data[] {
  const points: Vector3Data[] = [];
  const a = orbit.semiMajorAxisKm;
  const e = orbit.eccentricity;

  for (let i = 0; i < sampleCount; i += 1) {
    const trueAnomaly = (i / sampleCount) * Math.PI * 2;
    const r = (a * (1 - e * e)) / (1 + e * Math.cos(trueAnomaly));
    const orbital: Vector3Data = {
      x: r * Math.cos(trueAnomaly),
      y: r * Math.sin(trueAnomaly),
      z: 0,
    };
    points.push(rotateOrbitalToEcliptic(orbital, orbit));
  }

  return points;
}

export function calculateOrbitalSpeedKmS(
  distanceFromFocusKm: number,
  semiMajorAxisKm: number,
  parentGravitationalParameterKm3S2: number,
): number {
  if (distanceFromFocusKm <= 0 || semiMajorAxisKm <= 0) return 0;
  const v2 =
    parentGravitationalParameterKm3S2 *
    (2 / distanceFromFocusKm - 1 / semiMajorAxisKm);
  return Math.sqrt(Math.max(0, v2));
}

export function emptyPosition(): Vector3Data {
  return zeroVector();
}
